import React, { useState } from 'react'
import GenericModal from '../../../../components/GenericModal'
import Dropdown from '../../../../components/Dropdown'
import { CreateAndEditTermsSection } from '../CreateAndEditTermsSection'
import CsvMappingUpload from '../../../../components/CsvMappingUpload'
import { getTermsSectionsApi, getEnrollmentsApi } from '../../../../store/slices/termsSectionsSlice/api'
import { useAppDispatch } from '../../../../store'
import api from '../../../../utils/api'
import { exportCsv } from '../../../../utils/exportCsv'
import { notification, closeNotification } from '../../../../components/Notifications'
import { useParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { resetCourseSectionsByTermId } from '../../../../APIManager/TermsManager'
import { type ApiError } from '../../../../models/Common'
import { resetInstructorsByTermId } from '../../../../APIManager/UserManager'
import ExportProcessingModal from '../../../../components/ExportProcessingModal'
import { grey } from '../../../../colors'
import { ButtonType } from '../../../../components/GenericButton/styles'
import { TableModalAction } from '../../../../components/CustomTable/types'
import { getTermsSectionsList } from '../../../../store/slices/termsSectionsSlice/selectors'
import { type ISection } from '../../../../models/TermsSection'

interface TermsTopbarActionsProps {
  params: Record<string, any>
  allowedActions: string[]
  setExportTaskId: (id: string | null) => void
  setUploadTaskId: (id: string | null) => void
  checkStatusBeforeExport: () => Promise<boolean>
  isTaskRunning: boolean
  isUploadRunning: boolean
  onTaskBlock: () => void
}

type ResetTarget = 'sections' | 'instructors' | null

const resetMeta = {
  sections: {
    title: 'Reset Course Sections',
    body: 'Deletes course sections, instructor assignments, and  enrollments only if the section is not linked to a survey project or is linked to one in Draft status. This action cannot be undone.',
    tooltip: 'Deletes course sections, instructor assignments, and  enrollments only if the section is not linked to a survey project or is linked to one in Draft status. This action cannot be undone.',
    api: async (termId: string) => await resetCourseSectionsByTermId(termId),
    successMsg: (response: any) => { notification(response?.data?.message, { variant: 'success' }) }
  },
  instructors: {
    title: 'Reset Instructor Assignments',
    body: 'Deletes instructor assignments of course sections that do not have a survey in published, live or closed state. This action cannot be undone.',
    tooltip: 'Deletes instructor assignments of course sections that do not have a survey in published, live or closed state. This action cannot be undone.',
    successMsg: (response: any) => { notification(response?.data?.message, { variant: 'success' }) },
    api: async (termId: string) => await resetInstructorsByTermId(termId)
  }
} as const

const TermsTopbarActions: React.FC<TermsTopbarActionsProps> = ({ params, allowedActions, setExportTaskId, setUploadTaskId, checkStatusBeforeExport, isTaskRunning, isUploadRunning, onTaskBlock }) => {
  const [isUploadModal, setIsUploadModal] = useState<boolean>(false)
  const [isUploadRolesModal, setIsUploadRolesModal] = useState<boolean>(false)
  const [exportLoadingTarget, setExportLoadingTarget] = useState<null | 'sections' | 'instructors'>(null)
  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [exportModalMessage, setExportModalMessage] = useState('')
  const [isCreateModal, setIsCreateModal] = useState<boolean>(false)

  const dispatch = useAppDispatch()
  const { id } = useParams()
  const closeUploadModal = (): void => {
    setIsUploadModal(false)
  }

  const closeUploadRolesModal = (): void => {
    setIsUploadRolesModal(false)
  }

  const uploadApi = (): void => {
    if (isTaskRunning) { onTaskBlock(); return }
    setIsUploadModal(true)
  }

  const handleUploadRolesModal = (): void => {
    if (isTaskRunning) { onTaskBlock(); return }
    setIsUploadRolesModal(true)
  }

  const addManually = (): void => {
    if (isTaskRunning) { onTaskBlock(); return }
    setIsCreateModal?.(true)
  }

  const handleCourseExport = async (): Promise<void> => {
    if (isUploadRunning) { onTaskBlock(); return }
    const canExport = await checkStatusBeforeExport()
    if (!canExport) return
    setExportLoadingTarget('sections')
    const toastKey = notification('Preparing download...', { variant: 'loading', persist: true })
    const apiParams = { termId: id, search: params?.search, sorting: params?.sort }
    try {
      const response = await api.get('/api/sections/export/', { params: apiParams })
      if (response?.status === 202) {
        if (response?.data?.taskId) {
          setExportTaskId(response.data.taskId)
        }
        setExportModalMessage(response?.data?.message || '')
        setExportModalOpen(true)
        return
      }
      const data = response?.data
      if (data) {
        exportCsv({ data, headers: response?.headers, loadingToastKey: toastKey })
      } else {
        closeNotification(toastKey)
      }
    } catch (error: any) {
      closeNotification(toastKey)
      notification(error?.message ?? 'Internal server error', { variant: 'error' })
    } finally {
      setExportLoadingTarget(null)
    }
  }

  const handleInstructorExport = async (): Promise<void> => {
    if (isUploadRunning) { onTaskBlock(); return }
    const canExport = await checkStatusBeforeExport()
    if (!canExport) return
    setExportLoadingTarget('instructors')
    const toastKey = notification('Preparing download...', { variant: 'loading', persist: true })
    const exportParams = { termId: id, isInstructor: true }
    try {
      const response = await api.get('/api/users/roles/export/', { params: exportParams })
      if (response?.status === 202) {
        if (response?.data?.taskId) {
          setExportTaskId(response.data.taskId)
        }
        setExportModalMessage(response?.data?.message || '')
        setExportModalOpen(true)
        return
      }
      const data = response?.data
      if (data) {
        exportCsv({ data, headers: response?.headers, loadingToastKey: toastKey })
      } else {
        closeNotification(toastKey)
      }
    } catch (error: any) {
      closeNotification(toastKey)
      notification(error?.message ?? 'Internal server error', { variant: 'error' })
    } finally {
      setExportLoadingTarget(null)
    }
  }

  const AddCourseSectionMenuItems = [
    { text: 'Upload Course Sections (.csv)', action: uploadApi },
    { text: 'Upload Instructor Assignments (.csv)', action: handleUploadRolesModal },
    { text: 'Add Manually', action: addManually }
  ]

  const isExportDisabled = !!exportLoadingTarget || isTaskRunning

  const termDropdownActionMenus = [
    {
      text: 'Export Course Sections (.csv)',
      action: handleCourseExport,
      disabled: isExportDisabled,
      loading: exportLoadingTarget === 'sections',
      visable: allowedActions?.includes(TableModalAction.READ)
    },
    {
      text: 'Export Instructor Assignments (.csv)',
      action: handleInstructorExport,
      disabled: isExportDisabled,
      loading: exportLoadingTarget === 'instructors',
      visable: allowedActions?.includes(TableModalAction.READ)
    },
    {
      text: 'Reset Course Sections',
      action: () => { setResetTarget('sections') },
      disabled: isTaskRunning,
      tooltipTitle: resetMeta.sections.tooltip
    },
    {
      text: 'Reset Instructor Assignments',
      action: () => { setResetTarget('instructors') },
      disabled: isTaskRunning,
      tooltipTitle: resetMeta.instructors.tooltip
    }
  ]

  const [resetTarget, setResetTarget] = useState<ResetTarget>(null)
  const { mutate: resetMutation, isPending: isResetLoading } = useMutation({
    mutationFn: async (target: NonNullable<ResetTarget>) => {
      return await resetMeta[target].api(id ?? '')
    },
    onSuccess: (response, target) => {
      const apiMsg = response?.data?.message || resetMeta[target].successMsg
      notification(apiMsg, { variant: 'success' })
      void dispatch(getTermsSectionsApi({ params }))
      if (target === 'sections') {
        const { termId, ...enrollParams } = params ?? {}
        void dispatch(getEnrollmentsApi({ urlId: id, params: enrollParams }))
      }
    }
  })

  const handleConfirmReset = async (): Promise<void> => {
    if (!resetTarget) return
    resetMutation(resetTarget)
    setResetTarget(null)
  }

  const activeMeta = resetTarget ? resetMeta[resetTarget] : null

  const termsSectionList = getTermsSectionsList()
  const isSectionsEmpty = !termsSectionList?.data || termsSectionList.data.length === 0

  return (
    <>
      {allowedActions.includes(TableModalAction.CREATE) && <Dropdown menuItems={AddCourseSectionMenuItems} label='Add Course Sections' />}
      <GenericModal
        isOpen={isCreateModal}
        title={'Create Course Section'}
        onClose={() => { setIsCreateModal?.(false) }}
        maxWidth='sm'
        action={false}
      >
        <CreateAndEditTermsSection params={params} handleCloseModal={() => { setIsCreateModal?.(false) }} />
      </GenericModal>

      <CsvMappingUpload isOpen={isUploadModal} onClose={closeUploadModal} modalTitle="Course Sections" csvHeaderEndpoint="/api/sections/csv_headers" uploadCsvEndpoint={`/api/sections/?term_id=${params?.termId}`} hasCreatePermission={allowedActions.includes(TableModalAction.CREATE)} returnTaskId={(taskId) => { if (taskId) setUploadTaskId(taskId) }} onSuccess={() => {
        notification('Course sections successfully uploaded', { variant: 'success' })
        void dispatch(getTermsSectionsApi({ params }))
      }} />

      <CsvMappingUpload isOpen={isUploadRolesModal} onClose={closeUploadRolesModal} modalTitle="Instructors" csvHeaderEndpoint="/api/users/roles/csv_headers?is_faculty=true" uploadCsvEndpoint={`/api/users/roles/?is_faculty=True&term_id=${params?.termId}`} hasCreatePermission={allowedActions.includes(TableModalAction.CREATE)} returnTaskId={(taskId) => { if (taskId) setUploadTaskId(taskId) }} onSuccess={() => {
        notification('Instructor assignments successfully uploaded', { variant: 'success' })
        void dispatch(getTermsSectionsApi({ params }))
      }
      } />

      {allowedActions.includes(TableModalAction.DELETE) && (
        <Dropdown
          menuItems={termDropdownActionMenus}
          dropdownType='action'
          extraButtonProps={{
            style: { p: 0 },
            loading: isResetLoading,
            disabled: isResetLoading || isSectionsEmpty,
            circularProgressStyles: { color: grey[300] },
            buttonType: ButtonType.Ghost
          }}
          disabled={isSectionsEmpty}
        />
      )}
      <GenericModal
        isOpen={!!resetTarget}
        onClose={() => { setResetTarget(null) }}
        title={activeMeta?.title ?? ''}
        maxWidth='sm'
        onConfirm={handleConfirmReset}
        confirmButtonLoading={isResetLoading}
      >
        {activeMeta?.body ?? ''}
      </GenericModal>
      <ExportProcessingModal
        isOpen={exportModalOpen}
        onClose={() => { setExportModalOpen(false) }}
        message={exportModalMessage}
      />
    </>
  )
}

export default TermsTopbarActions
